import { useState } from 'react'
import { Tab, TabbedShowLayout, useNotify, useRefresh, useShowController } from 'react-admin'
import { useHistory } from 'react-router'
import { Accordion } from 'shared/components/Accordion/Accordion'
import { Divider } from 'shared/components/Base/Divider'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { BlueEditTextButton } from 'shared/components/Button/EditButton'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { DropZoneArea } from 'shared/components/DropZoneArea/DropZoneArea'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { Text } from 'shared/components/Text/Text'
import { formatMobile } from 'shared/helpers/formatters'
import { toTitleCase } from 'shared/helpers/utils'
import { darkGray, kellyGreenD1, lightGray1, marigoldOrange, success, warning } from 'shared/theme/colors'

import { uploadCarrierAgreement } from 'api/brokers'
import { StatusBadge } from 'components/Common/StatusBadge.styles'
import { BrokerCarrierRelationship } from 'models/broker'
import { CustomBooleanField } from '../Fields'
import { CustomBrokerShow } from './BrokerShow'
import {
  GeneralInformationGrid,
  LabelValueGrid,
  SectionWithDivider,
  StyledCarrierRelationshipSection,
} from './BrokerShowV2.styles'

interface BrokerCarrierRelatinshipSectionProps {
  carrier_relationships?: BrokerCarrierRelationship[]
}

const LabelValue = ({ label, value }: { label: string; value: string }) => {
  return (
    <>
      <Text variant="header3" fontColor={lightGray1}>
        {label}
      </Text>
      <Text variant="label2">{value}</Text>
    </>
  )
}

function toTitleCaseWithoutUnderscore(str: string) {
  return toTitleCase(str).replace('_', ' ')
}

const CarrierRelationshipStatusBackgroundColor = {
  APPROVAL_PENDING: warning,
  APPROVED: success,
  SUSPENDED: warning,
  DISQUALIFIED: warning,
  INVALID: warning,
  DNU: warning,
  LIMITED: warning,
}

const CarrierRelationshipStatusColor = {
  APPROVAL_PENDING: marigoldOrange,
  APPROVED: kellyGreenD1,
  SUSPENDED: marigoldOrange,
  DISQUALIFIED: marigoldOrange,
  INVALID: marigoldOrange,
  DNU: marigoldOrange,
  LIMITED: marigoldOrange,
}

const UploadNewCarrierAgreement = ({ record }: any) => {
  const [showDialog, setShowDialog] = useState(false)
  const [file, setFile] = useState<File | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const handleUpload = async () => {
    if (!file) return
    setIsLoading(true)
    const formData = new FormData()
    formData.append('carrier_agreement', file)
    try {
      await uploadCarrierAgreement(record.id, formData)
      setIsLoading(false)
      setShowDialog(false)
      refresh()
      notify('Carrier agreement uploaded', { type: 'success' })
    } catch (err) {
      notify('Failed to upload carrier agreement', { type: 'error' })
      setIsLoading(false)
    }
  }
  return (
    <FlexBox gap="8px" alignItems="flex-start">
      {record.carrier_agreement && (
        <>
          <Button variant="text" size="medium" onClick={() => window.open(record.carrier_agreement)}>
            View
          </Button>
          <Divider borderWidth="1px" height="20px" vertical />
        </>
      )}
      <Button variant="text" size="medium" onClick={() => setShowDialog(true)}>
        Upload new
      </Button>
      <SimpleDialog isOpen={showDialog} onClose={() => setShowDialog(false)}>
        <FlexBox alignItems="center" isColumn>
          <Text variant="header2">Upload new carrier agreement</Text>
          <Box mt={2} />
          <DropZoneArea onChange={setFile} onDelete={() => setFile(undefined)} />
          <Box mt={2} />
          {file && (
            <Button variant="primary" size="large" onClick={handleUpload} isLoading={isLoading}>
              Upload
            </Button>
          )}
        </FlexBox>
      </SimpleDialog>
    </FlexBox>
  )
}

const CarrierRelationshipSection = ({
  carrier_relationship,
  isLast,
}: {
  carrier_relationship: BrokerCarrierRelationship
  isLast: boolean
}) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
  const history = useHistory()
  return (
    <StyledCarrierRelationshipSection isColumn gap="8px" isLast={isLast}>
      <FlexBox justifyContent="space-between">
        <Text variant="header6">{carrier_relationship.carrier.name}</Text>
        <BlueEditTextButton
          ariaLabel="Edit broker carrier relationship"
          onClick={() => {
            history.push(`/broker-carrier-relationships/${carrier_relationship.id}/`)
          }}
        />
      </FlexBox>
      <FlexBox gap="100px">
        <LabelValueGrid>
          <Text variant="header3" fontColor={lightGray1}>
            Carrier-broker relationship
          </Text>
          <Text variant="label2">
            <StatusBadge
              $backgroundColor={CarrierRelationshipStatusBackgroundColor[carrier_relationship.status]}
              $color={CarrierRelationshipStatusColor[carrier_relationship.status]}
            >
              {toTitleCaseWithoutUnderscore(carrier_relationship.status)}
            </StatusBadge>
          </Text>
          {carrier_relationship.status_reason && (
            <LabelValue label="Status reason" value={carrier_relationship.status_reason} />
          )}
          <Text variant="header3" fontColor={lightGray1}>
            Packet complete?
          </Text>
          <CustomBooleanField source="is_carrier_packet_complete" record={carrier_relationship} />
          <Text variant="header3" fontColor={lightGray1}>
            Carrier agreement
          </Text>
          <UploadNewCarrierAgreement record={carrier_relationship} />
        </LabelValueGrid>
        <LabelValueGrid>
          <LabelValue
            label="Booking phone"
            value={formatMobile(carrier_relationship.booking_contact_phone)}
          />
          <LabelValue label="Booking email" value={carrier_relationship.booking_contact_email} />
          <LabelValue label="Support phone" value={formatMobile(carrier_relationship.support_main_phone)} />
          <Text variant="header3" fontColor={lightGray1}>
            Support email
          </Text>
          <Box>
            <Text variant="label2">{carrier_relationship.support_main_email}</Text>
            {carrier_relationship.use_support_email_for_tracking && (
              <Text variant="label4" fontColor={lightGray1}>
                Used to send job tracking info
              </Text>
            )}
          </Box>
        </LabelValueGrid>
      </FlexBox>
      <FlexBox mt={2} width="125px">
        <Accordion
          isExpanded={isAccordionExpanded}
          onClick={(isExpanded) => setIsAccordionExpanded(isExpanded)}
          align="flex-start"
          details={
            <FlexBox mt={2} width="600px">
              <LabelValueGrid>
                <LabelValue
                  label="Payment method"
                  value={toTitleCaseWithoutUnderscore(carrier_relationship.payment_method)}
                />
                <LabelValue
                  label="Automated invoicing"
                  value={carrier_relationship.is_invoicing_submission_automated ? 'Yes' : 'No'}
                />
              </LabelValueGrid>
              <LabelValueGrid>
                <LabelValue
                  label="Submission requirements"
                  value={carrier_relationship.submission_requirement_notes}
                />
              </LabelValueGrid>
            </FlexBox>
          }
        >
          {() => (
            <FlexBox>
              <Text variant="label2" fontColor={lightGray1}>
                Show {isAccordionExpanded ? 'less' : 'more'}
              </Text>
            </FlexBox>
          )}
        </Accordion>
      </FlexBox>
    </StyledCarrierRelationshipSection>
  )
}

const BrokerCarrierRelationshipSection = ({
  carrier_relationships,
}: BrokerCarrierRelatinshipSectionProps) => {
  return (
    <FlexBox isColumn mt={2} mb={2} ml={2} mr={2}>
      {carrier_relationships?.map((carrier_relationship, index) => (
        <CarrierRelationshipSection
          key={index}
          carrier_relationship={carrier_relationship}
          isLast={index === carrier_relationships.length - 1}
        />
      ))}
    </FlexBox>
  )
}

const GeneralInformationSection = ({ record }: any) => {
  return (
    <GeneralInformationGrid>
      <FlexBox isColumn>
        <SectionWithDivider isColumn>
          <LabelValueGrid>
            <Text variant="header3" fontColor={lightGray1}>
              Is shipper?
            </Text>
            <CustomBooleanField source="is_shipper" record={record} />
            {record.is_shipper && <LabelValue label="FEIN Number" value={record.fein_number} />}
            <Text variant="header3" fontColor={lightGray1}>
              Trucklists enabled?
            </Text>
            <CustomBooleanField source="trucklists_enabled" record={record} />
          </LabelValueGrid>
        </SectionWithDivider>
        <SectionWithDivider isColumn gap="10px">
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            CONTACT
          </Text>
          <LabelValueGrid>
            <LabelValue label="Pay status inquiry email" value={record.pay_status_inquiry_email} />
            <LabelValue label="VC invoicing email" value={record.bol_contact_email} />
            <LabelValue label="After hours phone" value={record.support_after_hours_phone} />
            <LabelValue label="After hours email" value={record.support_after_hours_email} />
            <LabelValue label="Factoring invoicing email" value={record.factoring_invoicing_email} />
            <LabelValue label="Factoring NOA email" value={record.factoring_noa_email} />
            <LabelValue label="Trucklists email" value={record.trucklists_email} />
            <LabelValue
              label="Job tracking submission method"
              value={toTitleCaseWithoutUnderscore(record.tracking_submission_method)}
            />
            <Text variant="header3" fontColor={lightGray1}>
              Billing address
            </Text>
            {record.billing_address && (
              <Text variant="label2">
                {record.billing_address.line1}
                <br />
                {record.billing_address.line2 && record.billing_address.line2}
                {record.billing_address.line2 && <br />}
                {record.billing_address.city}, {record.billing_address.state} {record.billing_address.zip}
              </Text>
            )}
          </LabelValueGrid>
        </SectionWithDivider>
        <FlexBox isColumn gap="10px">
          <Text variant="uppercaseLabel2" fontColor={darkGray}>
            INVOICING
          </Text>
          <LabelValueGrid>
            <LabelValue
              label="Invoice submission method"
              value={toTitleCaseWithoutUnderscore(record.bol_submission_method)}
            />
            <LabelValue label="Invoicing portal link" value={record.carrier_portal_link} />
            <LabelValue label="Payment terms days" value={record.payment_terms_days} />
            <LabelValue label="Layover rate / day" value={`$${record.layover_rate_per_day}`} />
            <LabelValue label="Detention rate / hour" value={`$${record.detention_rate_per_hour}`} />
            <LabelValue
              label="Detention start after checkin"
              value={record.detention_start_time_hours ? `${record.detention_start_time_hours}h` : ''}
            />
            <LabelValue
              label="Detention max / day"
              value={record.detention_max_hours_per_day ? `${record.detention_max_hours_per_day}h` : ''}
            />
            <LabelValue label="Tonu rate" value={`$${record.tonu_rate}`} />
          </LabelValueGrid>
        </FlexBox>
      </FlexBox>
      <FlexBox isColumn>
        <Text variant="uppercaseLabel2" fontColor={darkGray}>
          NOTES
        </Text>
        <Text variant="label2">{record.notes}</Text>
      </FlexBox>
    </GeneralInformationGrid>
  )
}

const BrokerShowV2 = (props: any) => {
  const { record } = useShowController(props)
  usePageTitle(record?.name)

  if (!record) return null

  return (
    <CustomBrokerShow {...props}>
      <TabbedShowLayout>
        <Tab label="CARRIER-BROKER INFORMATION">
          <BrokerCarrierRelationshipSection carrier_relationships={record.carrier_relationships} />
        </Tab>
        <Tab label="General information">
          <GeneralInformationSection record={record} />
        </Tab>
      </TabbedShowLayout>
    </CustomBrokerShow>
  )
}

export default BrokerShowV2
